import {
  ArrowRight,
  Check,
  Radar,
  ScanSearch,
  FlaskConical,
  History,
  Network,
  KeyRound,
  Bot,
  UserCheck,
  Globe,
  ShieldAlert,
  Plug,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import type { LucideIcon } from 'lucide-react';
import { LandingHeader } from '@/components/LandingHeader';
import { LandingFooter } from '@/components/LandingFooter';
import { LocalizedAnchor, LocalizedLink } from '@/components/LocalizedLink';
import { JsonLd, breadcrumbJsonLd } from '@/components/JsonLd';
import { buttonVariants } from '@/components/ui/Button';
import { useDocumentMeta } from '@/hooks/useDocumentMeta';
import { useT } from '@/i18n/provider';
import { SITE_LOCALE, SITE_ORIGIN, HREFLANG_TAGS } from '@/lib/site-config';
import { getCampaign, trackEvent } from '@/lib/analytics';

/**
 * Ad landing page for the ChatGPT Ads campaign around current threats
 * (exploited CVEs, attacks that start with a sign-in, AI agents attacking web
 * shops). One goal: sign up for Free. Every CTA reports
 * `threat_page_cta_click` with its position; the analytics layer attaches the
 * campaign (utm_*) to it and the signup link carries it to the app (ADR-0162).
 *
 * Copy rules for this page: every number has a public source linked next to
 * it, and every product claim maps to something that ships (ADR-0172 CVE to
 * detection, ADR-0173 Sigma shadow mode, ADR-0178 retro-hunt) or to a
 * built-in detection. The facts are dated: refresh them when the news moves on.
 */
const SIGNUP_HREF = 'https://app.logpulse.io/sign-up';
const PAGE_PATH = '/threat-intelligence';

/** Fact cards (news of October 2026). Update `value`/`label`/`source` keys together with the href. */
/** Date the facts below were last checked; also the page's dateModified. */
const FACTS_UPDATED = '2026-11-04';

const FACTS = [
  { key: 'f1', href: 'https://www.thestack.technology/cves-in-2025-analysis/' },
  { key: 'f2', href: 'https://thehackernews.com/2026/10/cisa-adds-actively-exploited-gitlab-rce.html' },
  { key: 'f3', href: 'https://www.computable.nl/2026/11/04/kort-aanval-kost-ahold-delhaize-250-miljoen-datalek-bij-leverancier-voor-rekening-van-bedrijf-en-meer/' },
  { key: 'f4', href: 'https://thehackernews.com/2026/10/hackers-use-autonomous-ai-agents-to.html' },
] as const;

const CHAIN: { key: string; icon: LucideIcon }[] = [
  { key: 'known', icon: Radar },
  { key: 'exposed', icon: ScanSearch },
  { key: 'detect', icon: FlaskConical },
  { key: 'lookback', icon: History },
];

const ACCESS_SIGNALS = ['i1', 'i2', 'i3', 'i4', 'i5'] as const;
const ACCESS_SOURCE = 'https://www.cm-alliance.com/cybersecurity-blog/biggest-cyber-attacks-data-breaches-ransomware-attacks-october-2026';

const AI_POINTS: { key: string; icon: LucideIcon }[] = [
  { key: 'a1', icon: Bot },
  { key: 'a2', icon: UserCheck },
  { key: 'a3', icon: ShieldAlert },
];

const DEMO_ROWS = ['r1', 'r2', 'r3', 'r4'] as const;

const FAQ = ['q1', 'q2', 'q3', 'q4', 'q5', 'q6'] as const;

/** Sources the sign-in signals read; names as the app's source guides list them (log-source-guides.ts). */
const SIGNIN_SOURCES = ['Microsoft Entra ID', 'Microsoft 365', 'Okta', 'Windows Security', 'Linux (sshd)', 'FortiGate', 'Palo Alto', 'pfSense'] as const;

/**
 * Articles that go deeper into the page's three stories, where they exist in
 * the visitor's language: Dutch guides on logpulse.nl, English blog posts on
 * logpulse.io. They link back here, so the cluster reinforces the page.
 */
const DEEP_DIVES: Partial<Record<string, { label: string; href: string }[]>> = {
  nl: [
    { label: "Uitgebuite CVE's: welke raken jou?", href: '/guides/uitgebuite-cve-welke-raken-jou' },
    { label: 'AI-agents en webwinkels', href: '/guides/ai-agents-webwinkels-aanvallen' },
    { label: 'Lekken via een inlog', href: '/guides/datalek-via-inlog-herkennen' },
  ],
  en: [
    { label: 'Which KEV CVEs are about you?', href: '/blog/gitlab-cisco-wordpress-kev-which-cves-are-about-you' },
    { label: 'AI agents vs web shops', href: '/blog/ai-agents-attacking-web-shops-what-shows-in-your-logs' },
    { label: 'Breaches that started with a login', href: '/blog/breaches-that-started-with-a-login' },
  ],
};

const DEMO_HREF = 'https://booking.logpulse.io/';

const START: { key: string; icon: LucideIcon }[] = [
  { key: 's1', icon: UserCheck },
  { key: 's2', icon: Plug },
  { key: 's3', icon: ScanSearch },
];

/**
 * Hero variant that matches the ad the visitor clicked. The campaign plan
 * (docs/marketing/chatgpt-ads-dreigingscampagne.md) tags each ad with
 * utm_content a-*, b-*, c-* or d-*: exploited CVEs (a-kev names the products),
 * sign-in attacks, AI agents, MSPs (whose secondary button books a demo).
 * The headline then repeats the ad's promise; everything below the hero is
 * the same page. Read from the URL only, nothing is stored. Prerendering has
 * no query, so search engines always get the default.
 */
type HeroVariant = 'default' | 'kev' | 'login' | 'ai' | 'msp';

function heroVariantFromUrl(): HeroVariant {
  if (typeof window === 'undefined') return 'default';
  // The analytics layer strips utm_* from the address bar once captured, so
  // ask it rather than the URL.
  const content = getCampaign().utm_content ?? '';
  if (content.startsWith('a-kev')) return 'kev';
  if (content.startsWith('b-')) return 'login';
  if (content.startsWith('c-')) return 'ai';
  if (content.startsWith('d-')) return 'msp';
  return 'default';
}

function SignupButton({ position, label, variant, size = '2xl' }: { position: string; label: string; variant: HeroVariant; size?: 'lg' | 'xl' | '2xl' }) {
  return (
    <a
      href={SIGNUP_HREF}
      onClick={() => trackEvent('threat_page_cta_click', { cta: position, variant })}
      className={buttonVariants({ variant: 'gradient', size })}
    >
      {label}
      <ArrowRight className="h-5 w-5 ml-1" />
    </a>
  );
}

/**
 * Illustrative notable for the sign-in and AI hero variants, so the picture
 * next to the headline shows the finding that headline promises. Both are
 * built-in detections (Password spraying, Web shell eval payload). Labelled
 * as an example.
 */
function NotableExample({ kind }: { kind: 'login' | 'ai' }) {
  const t = useT();
  return (
    <div className="rounded-2xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-950 shadow-xl overflow-hidden">
      <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 dark:border-gray-800">
        <span className="text-sm font-semibold text-gray-950 dark:text-white">{t('threatPage.notable.title')}</span>
        <span className="text-xs font-medium uppercase tracking-wider text-gray-500">{t('threatPage.demo.example')}</span>
      </div>
      <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-800">
        <div className="font-mono text-sm text-gray-950 dark:text-white">{kind === 'login' ? 'Password spraying' : 'Web shell eval payload'}</div>
        <div className="mt-1 text-sm text-gray-600 dark:text-gray-400">{t(`threatPage.notable.${kind}.summary`)}</div>
      </div>
      <dl className="divide-y divide-gray-200 dark:divide-gray-800">
        {(['l1', 'l2', 'l3'] as const).map((l) => (
          <div key={l} className="px-5 py-3">
            <dt className="text-sm font-medium text-gray-950 dark:text-white">{t(`threatPage.notable.${l}`)}</dt>
            <dd className="text-sm text-gray-600 dark:text-gray-400">{t(`threatPage.notable.${kind}.${l}`)}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

/**
 * Sign-up bar pinned to the bottom on phones once the hero is out of view.
 * ChatGPT ads are seen mostly in the app, so most ad visitors read this page
 * on a phone, where the next button is often a screen or two away.
 */
function MobileSignupBar({ variant }: { variant: HeroVariant }) {
  const t = useT();
  const [show, setShow] = useState(false);
  useEffect(() => {
    const onScroll = () => setShow(window.scrollY > 700);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  return (
    <div
      className={`md:hidden fixed inset-x-0 bottom-0 z-40 border-t border-gray-200 dark:border-gray-800 bg-white/95 dark:bg-gray-950/95 backdrop-blur px-4 py-3 transition-transform duration-300 ${show ? 'translate-y-0' : 'translate-y-full'}`}
      aria-hidden={!show}
    >
      <div className="flex items-center justify-between gap-3">
        <span className="text-sm text-gray-700 dark:text-gray-300">{t('threatPage.mobileBar.text')}</span>
        <SignupButton position="mobile_bar" variant={variant} size="lg" label={t('threatPage.hero.cta')} />
      </div>
    </div>
  );
}

/** Illustrative advisory card: one CVE walking the chain. Labelled as an example. */
function AdvisoryExample() {
  const t = useT();
  return (
    <div className="rounded-2xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-950 shadow-xl overflow-hidden">
      <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 dark:border-gray-800">
        <span className="text-sm font-semibold text-gray-950 dark:text-white">{t('threatPage.demo.title')}</span>
        <span className="text-xs font-medium uppercase tracking-wider text-gray-500">{t('threatPage.demo.example')}</span>
      </div>
      <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-800">
        <div className="font-mono text-sm text-gray-950 dark:text-white">{t('threatPage.demo.cve')}</div>
        <div className="mt-1 text-sm text-gray-600 dark:text-gray-400">{t('threatPage.demo.product')}</div>
      </div>
      <ol className="divide-y divide-gray-200 dark:divide-gray-800">
        {DEMO_ROWS.map((r, i) => (
          <li key={r} className="flex gap-3 px-5 py-3">
            <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full border-2 border-primary-500 text-xs font-bold text-primary-700 dark:text-primary-300">
              {i + 1}
            </span>
            <div className="min-w-0">
              <div className="text-sm font-medium text-gray-950 dark:text-white">{t(`threatPage.demo.${r}.label`)}</div>
              <div className="text-sm text-gray-600 dark:text-gray-400">{t(`threatPage.demo.${r}.value`)}</div>
            </div>
          </li>
        ))}
      </ol>
    </div>
  );
}

export function ThreatIntelligencePage() {
  const t = useT();
  const pageUrl = `${SITE_ORIGIN}${PAGE_PATH}`;
  const [variant] = useState<HeroVariant>(heroVariantFromUrl);
  const heroKey = variant === 'default' ? 'threatPage.hero' : `threatPage.hero.${variant}`;

  useDocumentMeta({
    title: t('threatPage.meta.title'),
    description: t('threatPage.meta.description'),
  });

  const webPageJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: t('threatPage.meta.title'),
    description: t('threatPage.meta.description'),
    url: pageUrl,
    inLanguage: HREFLANG_TAGS[SITE_LOCALE],
    isPartOf: { '@type': 'WebSite', name: 'LogPulse', url: SITE_ORIGIN },
    about: ['Threat intelligence', 'Vulnerability management', 'Detection engineering', 'SIEM'],
    keywords: 'CVE, CISA KEV, EPSS, threat intelligence, detection rules, retro-hunt, SIEM, NIS2',
    // Same date as the route manifest's lastmod: bump both when the facts are refreshed.
    dateModified: FACTS_UPDATED,
    publisher: { '@type': 'Organization', name: 'LogPulse', url: SITE_ORIGIN },
    // The public sources behind every number on the page, so search engines
    // and AI answers can see where the claims come from.
    citation: [...FACTS.map((f) => f.href), ACCESS_SOURCE].map((url) => ({ '@type': 'CreativeWork', url })),
  };

  const faqJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    inLanguage: HREFLANG_TAGS[SITE_LOCALE],
    mainEntity: FAQ.map((k) => ({
      '@type': 'Question',
      name: t(`threatPage.faq.${k}`),
      acceptedAnswer: { '@type': 'Answer', text: t(`threatPage.faq.a${k.slice(1)}`) },
    })),
  };

  const breadcrumb = breadcrumbJsonLd([
    { name: 'LogPulse', url: SITE_ORIGIN },
    { name: t('threatPage.hero.badge'), url: pageUrl },
  ]);

  return (
    <div className="min-h-screen bg-white dark:bg-gray-950">
      <JsonLd data={webPageJsonLd} />
      <JsonLd data={faqJsonLd} />
      <JsonLd data={breadcrumb} />
      <LandingHeader />

      {/* Hero */}
      <section className="relative pt-32 pb-16 sm:pt-40 sm:pb-24 gradient-mesh overflow-hidden">
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid gap-12 lg:grid-cols-2 lg:items-center">
          <div>
            <span className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300 border border-primary-200 dark:border-primary-800/30 px-2.5 py-1 rounded-full">
              <Radar className="h-3.5 w-3.5" />
              {t('threatPage.hero.badge')}
            </span>
            <h1 className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-gray-950 dark:text-white text-balance">
              {t(`${heroKey}.title`)}
            </h1>
            <p className="mt-6 text-lg sm:text-xl text-gray-600 dark:text-gray-400 text-balance">
              {t(`${heroKey}.subtitle`)}
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <SignupButton position="hero" variant={variant} label={t('threatPage.hero.cta')} />
              {variant === 'msp' ? (
                <a
                  href={DEMO_HREF}
                  className={buttonVariants({ variant: 'outline', size: '2xl' })}
                  onClick={() => trackEvent('threat_page_cta_click', { cta: 'demo_hero', variant })}
                >
                  {t('threatPage.hero.msp.ctaSecondary')}
                </a>
              ) : (
                <a
                  href="#chain"
                  className={buttonVariants({ variant: 'outline', size: '2xl' })}
                  onClick={() => trackEvent('threat_page_cta_click', { cta: 'how', variant })}
                >
                  {t('threatPage.hero.ctaSecondary')}
                </a>
              )}
            </div>
            {variant === 'msp' && (
              <p className="mt-4 text-sm text-gray-600 dark:text-gray-400">
                {t('threatPage.hero.msp.partner')}{' '}
                <LocalizedLink to="/reseller" className="font-medium text-primary-700 dark:text-primary-300 underline underline-offset-4">
                  {t('threatPage.hero.msp.partnerLink')}
                </LocalizedLink>
              </p>
            )}
            <ul className="mt-8 grid gap-2 sm:grid-cols-2 text-sm text-gray-700 dark:text-gray-300">
              {['trust1', 'trust2', 'trust3', 'trust4'].map((k) => (
                <li key={k} className="flex items-center gap-2">
                  <Check className="h-4 w-4 shrink-0 text-primary-600" aria-hidden />
                  {t(`threatPage.hero.${k}`)}
                </li>
              ))}
            </ul>
          </div>
          {variant === 'login' || variant === 'ai' ? <NotableExample kind={variant} /> : <AdvisoryExample />}
        </div>
      </section>

      {/* What is happening: sourced facts, no adjectives */}
      <section className="py-20 sm:py-24 bg-gray-50 dark:bg-gray-900/40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-gray-950 dark:text-white text-balance">
              {t('threatPage.facts.title')}
            </h2>
            <p className="mt-2 text-sm font-medium text-primary-700 dark:text-primary-300">{t('threatPage.facts.updated')}</p>
            <p className="mt-4 text-lg text-gray-600 dark:text-gray-400">{t('threatPage.facts.body')}</p>
          </div>
          <div className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {FACTS.map((f) => (
              <div key={f.key} className="flex flex-col rounded-2xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-950 p-6">
                <div className="font-display text-3xl font-extrabold text-primary-600">{t(`threatPage.facts.${f.key}.value`)}</div>
                <p className="mt-2 text-gray-700 dark:text-gray-300">{t(`threatPage.facts.${f.key}.label`)}</p>
                <a href={f.href} target="_blank" rel="noopener noreferrer" className="mt-auto pt-3 inline-block text-xs text-gray-500 hover:underline">
                  {t(`threatPage.facts.${f.key}.source`)}
                </a>
              </div>
            ))}
          </div>
          <p className="mt-8 max-w-3xl text-gray-600 dark:text-gray-400">{t('threatPage.facts.takeaway')}</p>
        </div>
      </section>

      {/* The chain */}
      <section id="chain" className="py-20 sm:py-28 scroll-mt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <p className="eyebrow">{t('threatPage.chain.eyebrow')}</p>
            <h2 className="mt-4 font-display text-3xl sm:text-4xl font-bold tracking-tight text-gray-950 dark:text-white text-balance">
              {t('threatPage.chain.title')}
            </h2>
            <p className="mt-4 text-lg text-gray-600 dark:text-gray-400">{t('threatPage.chain.subtitle')}</p>
          </div>
          <ol className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {CHAIN.map(({ key, icon: Icon }, i) => (
              <li key={key} className="rounded-2xl border border-gray-200 dark:border-gray-800 p-6">
                <div className="flex items-center gap-3">
                  <span className="flex h-8 w-8 items-center justify-center rounded-full border-2 border-primary-500 text-sm font-bold text-primary-700 dark:text-primary-300">
                    {i + 1}
                  </span>
                  <Icon className="h-5 w-5 text-primary-600" aria-hidden />
                </div>
                <h3 className="mt-4 font-semibold text-gray-950 dark:text-white">{t(`threatPage.chain.${key}.title`)}</h3>
                <p className="mt-2 text-gray-600 dark:text-gray-400">{t(`threatPage.chain.${key}.body`)}</p>
              </li>
            ))}
          </ol>
          <div className="mt-12 flex flex-col sm:flex-row sm:items-center gap-4">
            <SignupButton position="chain" variant={variant} label={t('threatPage.hero.cta')} />
            <p className="text-gray-600 dark:text-gray-400">{t('threatPage.chain.ctaNote')}</p>
          </div>
        </div>
      </section>

      {/* Sign-ins: where many of this month's breaches started */}
      <section className="py-20 sm:py-24 bg-gray-50 dark:bg-gray-900/40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid gap-12 lg:grid-cols-2 lg:items-start">
          <div>
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary-50 dark:bg-primary-900/30">
              <KeyRound className="h-5 w-5 text-primary-600" aria-hidden />
            </div>
            <h2 className="mt-4 font-display text-3xl sm:text-4xl font-bold tracking-tight text-gray-950 dark:text-white text-balance">
              {t('threatPage.access.title')}
            </h2>
            <p className="mt-4 text-lg text-gray-600 dark:text-gray-400">{t('threatPage.access.body')}</p>
            <a href={ACCESS_SOURCE} target="_blank" rel="noopener noreferrer" className="mt-3 inline-block text-xs text-gray-500 hover:underline">
              {t('threatPage.access.source')}
            </a>
            <p className="mt-4 text-gray-600 dark:text-gray-400">{t('threatPage.access.scope')}</p>
          </div>
          <div className="rounded-2xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-950 p-6 sm:p-8">
            <div className="flex items-center gap-3">
              <Network className="h-5 w-5 text-primary-600" aria-hidden />
              <h3 className="text-xl font-semibold text-gray-950 dark:text-white">{t('threatPage.access.listTitle')}</h3>
            </div>
            <ul className="mt-6 space-y-3">
              {ACCESS_SIGNALS.map((i) => (
                <li key={i} className="flex gap-3 text-gray-700 dark:text-gray-300">
                  <Check className="h-5 w-5 shrink-0 text-primary-600 mt-0.5" aria-hidden />
                  {t(`threatPage.access.${i}`)}
                </li>
              ))}
            </ul>
            <p className="mt-6 text-sm font-medium text-gray-950 dark:text-white">{t('threatPage.access.worksWith')}</p>
            <ul className="mt-3 flex flex-wrap gap-2">
              {SIGNIN_SOURCES.map((name) => (
                <li key={name} className="rounded-full border border-gray-200 dark:border-gray-800 px-3 py-1 text-xs text-gray-700 dark:text-gray-300">
                  {name}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* AI on both sides */}
      <section className="py-20 sm:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="max-w-3xl font-display text-3xl sm:text-4xl font-bold tracking-tight text-gray-950 dark:text-white text-balance">
            {t('threatPage.ai.title')}
          </h2>
          <p className="mt-4 max-w-3xl text-lg text-gray-600 dark:text-gray-400">{t('threatPage.ai.body')}</p>
          <div className="mt-12 grid gap-6 md:grid-cols-3">
            {AI_POINTS.map(({ key, icon: Icon }) => (
              <div key={key} className="rounded-2xl border border-gray-200 dark:border-gray-800 p-6">
                <Icon className="h-6 w-6 text-primary-600" aria-hidden />
                <h3 className="mt-4 font-semibold text-gray-950 dark:text-white">{t(`threatPage.ai.${key}.title`)}</h3>
                <p className="mt-2 text-gray-600 dark:text-gray-400">{t(`threatPage.ai.${key}.body`)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How to start */}
      <section className="py-20 sm:py-28 border-t border-gray-200/60 dark:border-gray-800/60">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="max-w-3xl font-display text-3xl sm:text-4xl font-bold tracking-tight text-gray-950 dark:text-white text-balance">
            {t('threatPage.start.title')}
          </h2>
          <p className="mt-4 max-w-3xl text-lg text-gray-600 dark:text-gray-400">{t('threatPage.start.subtitle')}</p>
          <ol className="mt-12 grid gap-6 md:grid-cols-3">
            {START.map(({ key, icon: Icon }, i) => (
              <li key={key} className="rounded-2xl border border-gray-200 dark:border-gray-800 p-6">
                <div className="flex items-center gap-3">
                  <span className="flex h-8 w-8 items-center justify-center rounded-full bg-primary-600 text-sm font-bold text-white">
                    {i + 1}
                  </span>
                  <Icon className="h-5 w-5 text-primary-600" aria-hidden />
                </div>
                <h3 className="mt-4 font-semibold text-gray-950 dark:text-white">{t(`threatPage.start.${key}.title`)}</h3>
                <p className="mt-2 text-gray-600 dark:text-gray-400">{t(`threatPage.start.${key}.body`)}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* Plans */}
      <section className="py-20 sm:py-24 bg-gray-50 dark:bg-gray-900/40">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Globe className="mx-auto h-6 w-6 text-primary-600" aria-hidden />
          <h2 className="mt-4 font-display text-3xl sm:text-4xl font-bold tracking-tight text-gray-950 dark:text-white text-balance">
            {t('threatPage.plans.title')}
          </h2>
          <p className="mt-4 text-lg text-gray-600 dark:text-gray-400">{t('threatPage.plans.body')}</p>
          <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
            <SignupButton position="plans" variant={variant} label={t('threatPage.hero.cta')} />
            <LocalizedLink to="/pricing" className={buttonVariants({ variant: 'outline', size: '2xl' })}>
              {t('threatPage.plans.pricing')}
            </LocalizedLink>
          </div>
          <p className="mt-8 text-gray-600 dark:text-gray-400">
            {t('threatPage.plans.nis2')}{' '}
            <LocalizedLink to="/compliance/nis2" className="text-primary-600 dark:text-primary-400 hover:underline">
              {t('threatPage.plans.nis2Link')}
            </LocalizedLink>
          </p>
          <p className="mt-3 text-gray-600 dark:text-gray-400">
            {t('threatPage.plans.demoLead')}{' '}
            <a
              href={DEMO_HREF}
              onClick={() => trackEvent('threat_page_cta_click', { cta: 'demo', variant })}
              className="text-primary-600 dark:text-primary-400 hover:underline"
            >
              {t('nav.bookDemo')}
            </a>
          </p>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 sm:py-24">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold tracking-tight text-gray-950 dark:text-white">{t('threatPage.faq.title')}</h2>
          <dl className="mt-8 divide-y divide-gray-200 dark:divide-gray-800">
            {FAQ.map((k) => (
              <div key={k} className="py-5">
                <dt className="font-semibold text-gray-950 dark:text-white">{t(`threatPage.faq.${k}`)}</dt>
                <dd className="mt-2 text-gray-600 dark:text-gray-400">{t(`threatPage.faq.a${k.slice(1)}`)}</dd>
              </div>
            ))}
          </dl>
          <div className="mt-10 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-5 text-sm">
            <span className="font-semibold text-gray-900 dark:text-white">{t('related.title')}</span>
            <div className="flex flex-wrap gap-x-5 gap-y-2">
              {[
                { label: 'Security Monitoring (SIEM)', href: '/siem' },
                { label: 'NIS2', href: '/compliance/nis2' },
                { label: 'Agentic SOC', href: '/agentic-soc' },
                ...(DEEP_DIVES[SITE_LOCALE] ?? []),
              ].map((r) => (
                <LocalizedAnchor
                  key={r.href}
                  href={r.href}
                  className="text-primary-600 dark:text-primary-400 hover:underline inline-flex items-center gap-1"
                >
                  {r.label}
                  <ArrowRight className="h-3.5 w-3.5" />
                </LocalizedAnchor>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24 sm:py-32 bg-gray-50 dark:bg-gray-900/40">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-gray-950 dark:text-white text-balance">
            {t('threatPage.final.title')}
          </h2>
          <p className="mt-4 text-lg text-gray-600 dark:text-gray-400">{t('threatPage.final.body')}</p>
          <div className="mt-8 flex justify-center">
            <SignupButton position="final" variant={variant} label={t('threatPage.hero.cta')} />
          </div>
        </div>
      </section>

      <LandingFooter />
      {/* Room for the pinned sign-up bar, so it never covers the footer's last line. */}
      <div className="h-20 md:hidden" aria-hidden />
      <MobileSignupBar variant={variant} />
    </div>
  );
}
